import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  GridReadyEvent,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem
} from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { VoucherQueryParams, VoucherQuerySort } from '../../../../pages/reporting/vouchers/types'
import { contextCompanyIdSelector } from '../../../../redux/context/company/selectors'
import { fetchTransactions } from '../../service/voucherService'
import { useDimensionQuery } from '../../../dimension/hooks/hooks'
import { getRequestParams } from '../../utils/voucherUtils'
import { TransactionWithVoucher, getTransactionWithVoucherColumnDefs } from './transactionWithVoucherColumnDefs'

export interface TransactionsTableProps {
  initialParams?: Partial<VoucherQueryParams>
  companyId?: string
  dimensions?: string
  height?: number | string
  showFilters?: boolean
  refreshKey?: number
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  initialParams,
  companyId: companyIdProp,
  dimensions,
  height = 800,
  showFilters = true,
  refreshKey
}) => {
  const { t } = useTranslation()
  const contextCompanyId = useSelector(contextCompanyIdSelector)
  const companyId = companyIdProp ?? contextCompanyId
  const dimensionQuery = useDimensionQuery()

  const gridApiRef = useRef<GridApi<TransactionWithVoucher>>()

  // Use refs to avoid recreating datasource on prop changes
  const initialParamsRef = useRef(initialParams)
  initialParamsRef.current = initialParams

  const effectiveDimensions = dimensions ?? dimensionQuery

  const [transactionColumnDefs, setTransactionColumnDefs] = useState(() =>
    getTransactionWithVoucherColumnDefs(t, [], showFilters)
  )

  // Update column defs when we get data with dimensions
  const updateColumnDefs = useCallback(
    (transactions: TransactionWithVoucher[]) => {
      const newColumnDefs = getTransactionWithVoucherColumnDefs(t, transactions, showFilters)
      setTransactionColumnDefs(newColumnDefs)
    },
    [t, showFilters]
  )

  const transformSortModel = useCallback((sortModel: SortModelItem[]): VoucherQuerySort[] | undefined => {
    if (sortModel.length === 0) return undefined

    return sortModel.map(item => {
      const field = item.colId
      // Map any specific field transformations here if needed

      return {
        field,
        direction: item.sort === 'asc' ? 'ASC' : 'DESC'
      }
    })
  }, [])

  const transformTransactionFilterModel = useCallback(
    (filterModel: Record<string, any>): Partial<VoucherQueryParams> => {
      const result: Partial<VoucherQueryParams> = {}

      if (filterModel.voucherGuid) {
        result.guid = filterModel.voucherGuid.filter
      } else if (showFilters) {
        result.guid = undefined
      }

      if (filterModel.voucherType) {
        result.voucherType = [filterModel.voucherType.filter]
      } else if (showFilters) {
        result.voucherType = undefined
      }

      if (filterModel.date?.dateFrom) {
        result.from = dayjs(filterModel.date.dateFrom)
      } else if (showFilters) {
        result.from = undefined
      }
      if (filterModel.date?.dateTo) {
        result.until = dayjs(filterModel.date.dateTo)
      } else if (showFilters) {
        result.until = undefined
      }

      if (filterModel.description) {
        result.transactionDesc = [filterModel.description.filter]
      } else if (showFilters) {
        result.transactionDesc = undefined
      }

      if (filterModel.invoiceNumber) {
        result.invoiceNumber = filterModel.invoiceNumber.filter
      } else if (showFilters) {
        result.invoiceNumber = undefined
      }

      return result
    },
    [showFilters]
  )

  const serverSideDatasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        if (!companyId) {
          params.fail()
          return
        }

        const { startRow = 0, endRow = 100, sortModel, filterModel } = params.request
        const pageSize = endRow - startRow
        const page = Math.floor(startRow / pageSize) + 1

        const sort = transformSortModel(sortModel)
        const filters = transformTransactionFilterModel(filterModel || {})

        const requestParams: Partial<VoucherQueryParams> = {
          ...initialParamsRef.current,
          ...filters,
          sort,
          page,
          pageSize,
          paginate: true
        }

        try {
          const result = await fetchTransactions(getRequestParams(requestParams), companyId, effectiveDimensions)
          const transactions = result.vouchers as TransactionWithVoucher[]

          // Update column definitions if we got transactions with dimensions
          if (transactions.length > 0) {
            updateColumnDefs(transactions)
          }

          params.success({
            rowData: transactions,
            rowCount: result.results
          })
        } catch (error) {
          console.error('Failed to fetch transactions:', error)
          params.fail()
        }
      }
    }),
    [companyId, effectiveDimensions, transformSortModel, transformTransactionFilterModel, updateColumnDefs]
  )

  // Helper to sync date filter with initialParams
  const syncDateFilter = useCallback(
    async (api: GridApi<TransactionWithVoucher>) => {
      if (showFilters && (initialParams?.from || initialParams?.until)) {
        const dateFilter: any = { filterType: 'date', type: 'inRange' }
        if (initialParams.from) {
          dateFilter.dateFrom = dayjs(initialParams.from).format('YYYY-MM-DD')
        }
        if (initialParams.until) {
          dateFilter.dateTo = dayjs(initialParams.until).format('YYYY-MM-DD')
        }
        await api.setColumnFilterModel('date', dateFilter)
        api.onFilterChanged()
      } else if (showFilters) {
        // Clear date filter if no initialParams date range
        await api.setColumnFilterModel('date', null)
        api.onFilterChanged()
      }
    },
    [initialParams, showFilters]
  )

  // Refresh transactions grid when initialParams or refreshKey change
  useEffect(() => {
    if (gridApiRef.current) {
      syncDateFilter(gridApiRef.current).then(() => {
        gridApiRef.current?.refreshServerSide({ purge: true })
      })
    }
  }, [initialParams, refreshKey, syncDateFilter])

  const onTransactionGridReady = useCallback(
    (event: GridReadyEvent<TransactionWithVoucher>) => {
      gridApiRef.current = event.api
      // Sync initial date filter
      syncDateFilter(event.api)
    },
    [syncDateFilter]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <CustomAgGrid
          columnDefs={transactionColumnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: false,
            suppressHeaderMenuButton: true
          }}
          onGridReady={onTransactionGridReady}
          getRowId={params => String(params.data.guid)}
          rowModelType="serverSide"
          serverSideDatasource={serverSideDatasource}
          cacheBlockSize={100}
          cellSelection
          pagination
          paginationPageSize={20}
          paginationPageSizeSelector={[20, 50, 100]}
          tooltipShowDelay={500}
          statusBar={{
            statusPanels: [
              { statusPanel: 'agTotalRowCountComponent' },
              { statusPanel: 'agSelectedRowCountComponent' },
              { statusPanel: 'agAggregationComponent' }
            ]
          }}
        />
      </div>
    </div>
  )
}

export default TransactionsTable
